import { zodResolver } from '@hookform/resolvers/zod';

import { useForm } from 'react-hook-form';

import { Divider, Typography } from '@mui/material';

import pMap from 'p-map';

import { useQuery } from '@apollo/client/react';

import { useOr2Editor } from '@/modules/editor/hooks/useOr2Editor';

import { useMutation } from '@/hooks/mutationWithSnackbar';
import { graphql } from '@/gql';
import { Or2Button } from '@/components/button/Or2Button';

import { CREATE_POST_MUTATION } from '@/app/(main)/g/[machineName]/noticeboard/_components/noticeboard/NoticeboardPoster';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import {
  ShareToNoticeboardForm,
  ShareToNoticeboardSchema,
} from '../../../group/noticeboard/ShareToNoticeboardForm';
import type { ShareToNoticeboardFormData } from '../../../group/noticeboard/ShareToNoticeboardForm';

export const SHARE_ARTICLE_TO_NOTICEBOARD_QUERY = graphql(`
  query ShareToArticleNoticeboard {
    currentUser {
      _id
      shortcutGroupsWithServiceAdminPermissionsConnection(
        first: null
        after: ""
        filterBy: {}
      ) {
        edges {
          node {
            _id
            name
            iconUrl
            hasKsg
            type {
              hasPosts
            }
          }
        }
      }
    }
  }
`);

interface Props {
  title: string;
  link: string;
  open: boolean;
  onClose: () => void;
}

export const ShareArticleToNoticeboardDialog = ({
  title,
  link,
  open,
  onClose,
}: Props) => {
  const { control, handleSubmit, setValue, watch, reset } =
    useForm<ShareToNoticeboardFormData>({
      defaultValues: {
        htmlContent: `Have a look at this:<br /><a href="${link ?? ''}">${title ?? ''}</a>`,
        groups: [],
      },
      resolver: zodResolver(ShareToNoticeboardSchema),
    });

  const { loading: loadingQuery, data } = useQuery(
    SHARE_ARTICLE_TO_NOTICEBOARD_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const { currentUser } = data ?? {};
  const { shortcutGroupsWithServiceAdminPermissionsConnection } =
    currentUser ?? {};

  const groupOptions = extractConnectionProps(
    shortcutGroupsWithServiceAdminPermissionsConnection,
  ).map((g) => {
    return {
      ...g,
      hasPosts: g.type?.hasPosts ?? false,
    };
  });

  const [createPost, { loading: loadingMutation }] = useMutation(
    CREATE_POST_MUTATION,
    {
      completionMessage: 'Post created',
      onCompleted: onClose,
    },
  );

  const loading = loadingQuery || loadingMutation;

  const editor = useOr2Editor({
    htmlContent: watch('htmlContent'),
    editable: !loading,
    onChange: (htmlContent) => setValue('htmlContent', htmlContent),
  });

  const onSubmit = async (data: ShareToNoticeboardFormData) => {
    await pMap(
      data.groups,
      async (group) => {
        await createPost({
          variables: {
            data: {
              htmlContent: data.htmlContent,
              content: null,
              editorContent: null,
              embeds: null,
              notifications: null,
              title: null,
              group: group,
              subgroup: null,
            },
          },
        });
      },
      { concurrency: 3 },
    );
  };

  return (
    <Or2Dialog
      keepMounted
      title="Share article to noticeboard"
      loading={loading}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button onClick={handleSubmit(onSubmit)}>Share</Or2Button>
        </>
      }
      slotProps={{
        transition: {
          onEnter: () => reset(),
        },
      }}
      open={open}
      onClose={onClose}
    >
      <Typography>Edit the text below:</Typography>
      <Divider />
      <ShareToNoticeboardForm
        control={control}
        editor={editor}
        groupOptions={groupOptions}
      />
    </Or2Dialog>
  );
};
